import React from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import GoogleMap from "@/components/GoogleMap";
import NearbySuburbs from "@/components/NearbySuburbs";
import RelatedServiceLinks from "@/components/RelatedServiceLinks";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { locationConfigs } from "@/data/locationData";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import {
    MapPin,
    CheckCircle,
    Users,
    Heart,
    Shield,
    Star,
    Award
} from "lucide-react";


const broadmeadowsFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Where is Y3 Smiles Dental located in Broadmeadows?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Y3 Smiles Dental is located at Unit 1, 34 King William Street, Broadmeadows VIC 3047 — near the Broadmeadows Shopping Centre and easily accessible from the Hume Freeway. Free parking is available on site.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Y3 Smiles Dental accept emergency dental appointments in Broadmeadows?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We offer same-day emergency dental appointments for urgent dental pain, broken teeth, facial swelling, and dental trauma. Call 03 9022 4442 and we will do our best to see you the same day.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Y3 Smiles Dental accept the Child Dental Benefits Schedule (CDBS)?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Y3 Smiles Dental is an approved CDBS provider. Eligible children aged 0–17 years can receive up to $1,158 in dental benefits over 2 calendar years. We check eligibility and process claims directly.',
      },
    },
    {
      '@type': 'Question',
      name: 'What health funds does Y3 Smiles Dental accept in Broadmeadows?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We accept all major Australian health funds including Bupa, Medibank, HCF, NIB (First Choice Network provider), CBHS, and many others. HICAPS instant claiming is available on site.',
      },
    },
    {
      '@type': 'Question',
      name: 'What are Y3 Smiles Dental\u2019s opening hours in Broadmeadows?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We are open Monday to Friday 9:00 AM \u2013 5:30 PM and Saturday 9:00 AM \u2013 1:00 PM. Closed Sunday.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Y3 Smiles Dental offer dental implants in Broadmeadows?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We offer dental implants as a permanent tooth replacement solution. Our principal dentist Dr Fatima Kurnaz provides a full implant consultation and personalised treatment plan. Contact us on 03 9022 4442 to book a consultation.',
      },
    },
  ],
};

// NOTE: Gatsby v4+ `export const Head` API doesn't work on Gatsby 3.15 (this project),
// so the SEO and FAQ schema must render from inside the page body via react-helmet-async.
// See <SEO> usage in the JSX below.

const BroadmeadowsPage = () => {
    useScrollAnimation();

    const services = [
        "Family Dentistry: Fillings, dentures, extractions, root canal treatments",
        "Children's Dentistry: Fluoride applications, oral hygiene education, fissure sealants",
        "Cosmetic Dentistry: Veneers, teeth whitening, crowns, reshaping teeth",
        "Gentle Care Options: Designed for anxious or nervous patients",
        "Emergency Dentistry: Prompt care for unexpected pain and injuries",
        "Preventive Care: Regular check-ups and professional cleanings"
    ];

    const whyChooseUs = [
        {
            icon: Users,
            title: "Personalised Care",
            description: "Our dentists take time to listen, review your history, and tailor treatment to your needs."
        },
        {
            icon: Heart,
            title: "Comfortable Environment",
            description: "A calming clinic and caring team ensure your visits are stress-free."
        },
        {
            icon: Shield,
            title: "Comprehensive Services",
            description: "From check-ups and preventative care to cosmetic dentistry and gentle treatment options."
        },
        {
            icon: Award,
            title: "Experienced Team",
            description: "Our skilled dentists stay updated with the latest techniques and technology."
        }
    ];

    return (
        <div className="min-h-screen flex flex-col">
            <SEO
              title="Dentist Broadmeadows VIC | Y3 Smiles Dental — Family & Emergency"
              description="Your trusted family dentist in Broadmeadows. Same-day emergency appointments, CDBS for kids, all health funds accepted. Book online today."
              canonicalUrl="https://y3smilesdental.com.au/locations/broadmeadows/"
            >
              <script type="application/ld+json">{JSON.stringify(broadmeadowsFaqSchema)}</script>
            </SEO>
            <Header />
            <main className="flex-grow">
                {/* Enhanced Hero Section */}
                <section className="relative bg-gradient-to-br from-neutral-50 via-primary/5 to-neutral-100 py-32 overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-primary"></div>
                        <div className="absolute top-40 right-20 w-20 h-20 rounded-full bg-secondary"></div>
                        <div className="absolute bottom-20 left-1/4 w-16 h-16 rounded-full bg-primary"></div>
                        <div className="absolute bottom-32 right-1/3 w-24 h-24 rounded-full bg-secondary"></div>
                    </div>

                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-5xl mx-auto">
                            <div className="text-center mb-12">
                                <div className="inline-flex items-center justify-center mb-8 p-4 bg-white/90 backdrop-blur-sm rounded-2xl shadow-soft">
                                    <img src="/location.png" alt="Location" className="w-10 h-10 mr-4" />
                                    <div className="text-left">
                                        <p className="text-sm font-medium text-primary uppercase tracking-wider">Dentist serving Broadmeadows</p>
                                        <p className="text-lg font-bold text-neutral-800">Broadmeadows</p>
                                    </div>
                                </div>

                                <h1 className="text-6xl md:text-7xl font-bold text-neutral-800 mb-6 leading-tight">
                                    Dentist serving{" "}
                                    <span className="block text-primary">Broadmeadows</span>
                                </h1>

                                <p className="text-xl text-neutral-800 mb-12 leading-relaxed max-w-3xl mx-auto">
                                    Your local dentist for check-ups, cosmetic treatments, and emergency care. We combine
                                    modern technology with gentle, personalised dentistry for the whole family.
                                </p>
                            </div>

                            {/* Enhanced Feature Pills */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft hover:shadow-medium transition-all duration-300 group">
                                    <CheckCircle className="w-6 h-6 text-primary mr-3 group-hover:scale-110 transition-transform" />
                                    <span className="font-semibold text-neutral-800">Local Broadmeadows Clinic</span>
                                </div>
                                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft hover:shadow-medium transition-all duration-300 group">
                                    <Award className="w-6 h-6 text-primary mr-3 group-hover:scale-110 transition-transform" />
                                    <span className="font-semibold text-neutral-800">Expert Dental Team</span>
                                </div>
                                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft hover:shadow-medium transition-all duration-300 group">
                                    <Heart className="w-6 h-6 text-primary mr-3 group-hover:scale-110 transition-transform" />
                                    <span className="font-semibold text-neutral-800">Family Friendly Care</span>
                                </div>
                            </div>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <Button variant="booking" size="xl" asChild className="group">
                                    <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                        <CheckCircle className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                        BOOK APPOINTMENT
                                    </a>
                                </Button>
                                <Button variant="booking" size="xl" asChild>
                                    <Link to="/treatments">
                                        View Our Treatments
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Enhanced About Section */}
                <section className="py-32 bg-gradient-to-b from-white to-neutral-50 relative">
                    {/* Decorative Elements */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-primary opacity-20"></div>

                    <div className="container mx-auto px-6">
                        <div className="max-w-7xl mx-auto">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                                <div className="space-y-8">
                                    <div className="space-y-4">
                                        <h2 className="text-2xl font-semibold text-primary uppercase tracking-widest mb-4">
                                            Patient-Centered Care
                                        </h2>
                                        <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 leading-tight">
                                            We Care About
                                            <span className="block text-primary">Your Smile</span>
                                        </h2>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="bg-white p-6 rounded-2xl shadow-soft border-l-4 border-primary">
                                            <p className="text-lg text-neutral-700 leading-relaxed">
                                                At Y3 Smiles Dental in Broadmeadows, we believe good dental care is more than just brushing
                                                and flossing. Regular check-ups are essential for maintaining healthy teeth and gums.
                                            </p>
                                        </div>

                                        <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-6 rounded-2xl">
                                            <p className="text-lg text-neutral-700 leading-relaxed">
                                                We're committed to affordable, gentle, and effective dentistry for the whole family.
                                                Our modern approach combines cutting-edge technology with compassionate care.
                                            </p>
                                        </div>

                                        <div className="bg-primary/10 p-6 rounded-2xl border border-primary/20">
                                            <p className="text-lg text-primary font-semibold leading-relaxed">
                                                Book your appointment today — whether you need a check-up, restorative care,
                                                or want to enhance your smile, we're here to help.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <Button variant="booking" size="lg" asChild className="group">
                                            <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                                <CheckCircle className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                                BOOK CONSULTATION
                                            </a>
                                        </Button>
                                        <Button variant="booking" size="lg" asChild>
                                            <Link to="#services">Learn More About Our Services</Link>
                                        </Button>
                                    </div>
                                </div>

                                <div className="relative">
                                    {/* Decorative Frame */}
                                    <div className="absolute -top-6 -left-6 w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl"></div>
                                    <div className="relative bg-white p-2 rounded-3xl shadow-2xl">
                                        <div className="aspect-[4/3] bg-neutral-100 rounded-2xl overflow-hidden">
                                            <img
                                                src="/broadmeadows.png"
                                                alt="Modern dental office in Broadmeadows"
                                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                            />
                                        </div>
                                    </div>

                                    {/* Floating Stats */}
                                    {/* <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl border border-primary/10">
                                        <div className="text-center">
                                            <div className="text-3xl font-bold text-primary">500+</div>
                                            <div className="text-sm text-neutral-800 font-medium">Happy Patients</div>
                                        </div>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Enhanced Why Choose Us Section */}
                <section className="py-32 bg-gradient-to-br from-neutral-50 to-primary/5 relative overflow-hidden">

                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center mb-20">
                                <h2 className="text-2xl font-semibold text-primary uppercase tracking-widest mb-4">
                                    Excellence in Care
                                </h2>

                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6 leading-tight">
                                    Why Choose
                                    <span className="block text-primary">
                                        Y3 Smiles Dental?
                                    </span>
                                </h2>

                                <p className="text-xl text-neutral-800 max-w-4xl mx-auto leading-relaxed">
                                    Experience the difference that expert care, modern technology, and genuine compassion
                                    make for Broadmeadows families. Here's what sets us apart.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                                {whyChooseUs.map((item, index) => {
                                    const IconComponent = item.icon;
                                    return (
                                        <div key={index} className="group bg-white p-8 rounded-3xl shadow-soft hover:shadow-2xl transition-all duration-500 border border-primary/5 hover:border-primary/20 relative overflow-hidden">
                                            {/* Background Gradient */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-secondary/0 group-hover:from-primary/5 group-hover:to-secondary/5 transition-all duration-500"></div>

                                            <div className="relative z-10">
                                                <div className="flex items-center mb-6">
                                                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 text-primary flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300 shadow-soft">
                                                        <IconComponent className="w-8 h-8" />
                                                    </div>
                                                    <div className="w-12 h-1 bg-gradient-to-r from-primary to-secondary rounded-full group-hover:w-16 transition-all duration-300"></div>
                                                </div>

                                                <h3 className="text-xl font-bold text-neutral-800 mb-4 group-hover:text-primary transition-colors duration-300">
                                                    {item.title}
                                                </h3>                                                <p className="text-neutral-800 leading-relaxed text-lg">
                                                    {item.description}
                                                </p>
                                            </div>

                                            {/* Decorative Element */}
                                            <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-full group-hover:scale-125 transition-transform duration-500 opacity-50"></div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Call to Action */}
                            <div className="text-center mt-16">
                                <div className="inline-flex flex-col sm:flex-row gap-4 items-center">
                                    <Button variant="booking" size="xl" asChild className="group">
                                        <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                            <Heart className="w-5 h-5 mr-2 group-hover:scale-125 transition-transform" />
                                            EXPERIENCE THE DIFFERENCE
                                        </a>
                                    </Button>

                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Enhanced Services Section */}
                <section id="services" className="py-32 bg-gradient-to-b from-white to-neutral-50 relative">
                    <div className="container mx-auto px-6">
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center mb-20">
                                <h2 className="text-2xl font-semibold text-primary uppercase tracking-widest mb-4">
                                    Our Treatments
                                </h2>

                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6 leading-tight">
                                    Our Dental Services in
                                    <span className="block text-primary">Broadmeadows</span>
                                </h2>

                                <p className="text-xl text-neutral-800 mb-8 max-w-4xl mx-auto leading-relaxed">
                                    From routine check-ups to advanced treatments, we provide comprehensive dental
                                    care for every member of your family in a comfortable, modern environment.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                                {services.map((service, index) => {
                                    const [title, description] = service.split(': ');
                                    return (
                                        <div key={index} className="group bg-white p-8 rounded-3xl shadow-soft hover:shadow-xl transition-all duration-300 border border-primary/5 hover:border-primary/20 relative overflow-hidden">
                                            {/* Background Effect */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-secondary/0 group-hover:from-primary/5 group-hover:to-secondary/5 transition-all duration-300"></div>

                                            <div className="relative z-10">
                                                <div className="flex items-center mb-6">
                                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                                                        <CheckCircle className="w-6 h-6 text-primary" />
                                                    </div>
                                                    <div className="w-8 h-1 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
                                                </div>

                                                <h3 className="text-xl font-bold text-neutral-800 mb-3 group-hover:text-primary transition-colors duration-300">
                                                    {title}
                                                </h3>

                                                <p className="text-neutral-800 leading-relaxed">
                                                    {description}
                                                </p>
                                            </div>

                                            {/* Hover Effect */}
                                            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Enhanced CTA Section */}
                            <div className="bg-gradient-to-r from-primary/10 via-white to-secondary/10 p-12 rounded-3xl border border-primary/20 text-center">
                                <h3 className="text-3xl font-bold text-neutral-800 mb-4">
                                    Ready to Transform Your Smile?
                                </h3>
                                <p className="text-lg text-neutral-800 mb-8 max-w-2xl mx-auto">
                                    Discover our complete range of dental treatments designed to give you
                                    the healthy, beautiful smile you deserve.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                    <Button variant="booking" size="xl" asChild className="group">
                                        <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                            <Star className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                            BOOK YOUR CONSULTATION
                                        </a>
                                    </Button>
                                    <Button variant="booking" size="xl" asChild>
                                        <Link to="/treatments">
                                            <Shield className="w-5 h-5 mr-2" />
                                            VIEW ALL TREATMENTS
                                        </Link>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <RelatedServiceLinks suburbName="Broadmeadows" suburbSlug="broadmeadows" className="bg-neutral-50" />

                {/* Linked Treatments Grid */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-5xl mx-auto">
                            <div className="text-center mb-10">
                                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-3">
                                    Dental Treatments in Broadmeadows
                                </h2>
                                <p className="text-lg text-neutral-700">
                                    We offer a full range of dental services for every member of your family.
                                </p>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-8">
                                {[
                                    { label: "Dental Implants", path: "/treatments/dental-implants" },
                                    { label: "Emergency Dentistry", path: "/treatments/emergency" },
                                    { label: "Kids Dentistry (CDBS)", path: "/treatments/childrens-dentistry" },
                                    { label: "Teeth Whitening", path: "/treatments/teeth-whitening" },
                                    { label: "Crown & Bridge", path: "/treatments/crown-bridge" },
                                    { label: "Root Canal", path: "/treatments/root-canal" },
                                    { label: "Wisdom Teeth", path: "/treatments/wisdom-teeth" },
                                    { label: "Dental Fillings", path: "/treatments/dental-fillings" },
                                ].map((t) => (
                                    <Link
                                        key={t.path}
                                        to={t.path}
                                        className="flex items-center gap-2 px-4 py-3 bg-neutral-50 border border-primary/20 rounded-xl text-sm font-medium text-neutral-800 hover:bg-primary hover:text-white hover:border-primary transition-all"
                                    >
                                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                                        {t.label}
                                    </Link>
                                ))}
                            </div>
                            <div className="text-center">
                                <Button variant="outline" asChild>
                                    <Link to="/treatments">View All Treatments <Shield className="w-4 h-4 ml-2 inline" /></Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Getting Here Section */}
                <section className="py-16 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl font-bold text-neutral-800 mb-6 text-center">Getting to Y3 Smiles Dental</h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="bg-white rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-xl font-semibold text-neutral-800 mb-4 flex items-center gap-2">
                                        <MapPin className="w-5 h-5 text-primary" /> Address &amp; Parking
                                    </h3>
                                    <p className="text-neutral-700 mb-2">Unit 1, 34 King William Street, Broadmeadows VIC 3047</p>
                                    <p className="text-neutral-700 mb-4">Free on-site parking is available directly in front of the clinic.</p>
                                    <p className="text-neutral-700">
                                        We are conveniently located near the <strong>Broadmeadows Shopping Centre</strong> and close to the <strong>Hume Freeway (M31)</strong>,
                                        making us easy to reach from Meadow Heights, Campbellfield, Greenvale, Glenroy and all surrounding suburbs.
                                    </p>
                                </div>
                                <div className="bg-white rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-xl font-semibold text-neutral-800 mb-4">Public Transport</h3>
                                    <ul className="space-y-2 text-neutral-700">
                                        <li>🚆 <strong>Broadmeadows Station</strong> is a short drive away (Craigieburn/Sunbury line)</li>
                                        <li>🚌 Multiple bus routes service King William Street</li>
                                        <li>🚗 Easy freeway access via Sydney Road and Broadmeadows Road exits</li>
                                    </ul>
                                    <div className="mt-4">
                                        <a
                                            href="https://maps.app.goo.gl/XCeiAYziTDqGdJpc8"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary font-semibold hover:underline"
                                        >
                                            Get Directions on Google Maps →
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-16 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-3xl mx-auto">
                            <h2 className="text-3xl font-bold text-neutral-800 mb-8 text-center">Frequently Asked Questions</h2>
                            <div className="space-y-4">
                                {[
                                    {
                                        q: "Do you offer same-day emergency dental appointments?",
                                        a: "Yes — we reserve time in our daily schedule for urgent dental cases. Call 03 9022 4442 and we will do our best to see you the same day for severe pain, broken teeth, swelling, or dental trauma."
                                    },
                                    {
                                        q: "Is CDBS (free kids dental) available at your Broadmeadows clinic?",
                                        a: "Yes. We are an approved CDBS provider. Eligible children aged 0–17 on Medicare can receive up to $1,158 in dental benefits over 2 calendar years covering check-ups, cleans, X-rays, fillings and extractions. We check your child's eligibility and bulk-bill directly."
                                    },
                                    {
                                        q: "Which health funds do you accept?",
                                        a: "We accept all major Australian health funds including Bupa, Medibank, HCF, NIB (we are a NIB First Choice Network provider), CBHS, and others. HICAPS instant claiming is available so you only pay the gap."
                                    },
                                    {
                                        q: "What payment plan options are available?",
                                        a: "We offer interest-free payment plans up to 12 months for treatments over $300, and extended plans up to 24 months with low interest. We also accept cash, EFTPOS, Visa and Mastercard."
                                    },
                                    {
                                        q: "Do you have a Turkish-speaking dentist?",
                                        a: "Yes. Dr Fatima Kurnaz, our principal dentist and founder, is bilingual in Turkish and English, making her a great choice for Turkish-speaking patients who prefer to discuss their care in their first language."
                                    },
                                ].map((item, i) => (
                                    <div key={i} className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                        <h3 className="text-lg font-semibold text-neutral-800 mb-2">{item.q}</h3>
                                        <p className="text-neutral-700 leading-relaxed">{item.a}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <NearbySuburbs suburbSlug="broadmeadows" />

                <GoogleMap
                    locationName="Broadmeadows"
                    locationData={locationConfigs.broadmeadows.locationData}
                    openingHours={locationConfigs.broadmeadows.openingHours}
                    className="bg-primary/5"
                />

                {/* <Contact /> */}
            </main>
            <Footer />
        </div>
    );
};

export default BroadmeadowsPage;